import { useAuth } from '@/lib/auth';
import { useConversation } from '@elevenlabs/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { WelcomeBanner } from './WelcomeBanner';
import { ReactComponent as SendIcon } from '@/assets/img/emily-send-icon.svg';
import { ReactComponent as AudioIcon } from '@/assets/img/emily-mic.svg';
import { ReactComponent as AudioMutedIcon } from '@/assets/img/emily-mic-muted.svg';
import { ReactComponent as CloseIcon } from '@/assets/img/close-btn.svg';
import Utils from '@/utils/util';

import Lottie from 'lottie-react';
import animationDataBg from '@/assets/animation/emily-bg.json'; // Adjust the path to your JSON file

import './ElevenslabAssistant.scss';
import { Button } from '@/components/Elements';
import { AudioStatus } from './AudioStatus';
import { TranscriptMessage, useGetConversationDetails } from '../api/getConversationDetails';
import { useGetSignedUrl } from '../api/getSignedUrl';
import { useGetConversationToken } from '../api/getConversationToken';
import { ConversationDetail } from './ChatAssistantV1';

type ElevenChatVoiceProps = {
  onChatLoaded: (loaded: boolean) => void;
  conversationDetail?: ConversationDetail;
  onClearChat?: () => void;
  showHistory: () => void;
};

export function ElevenChatVoice({
  onChatLoaded,
  conversationDetail,
  onClearChat,
  showHistory,
}: ElevenChatVoiceProps) {
  const conversationDetailMutation = useGetConversationDetails();
  const signedUrlMutation = useGetSignedUrl();
  const conversationTokenMutation = useGetConversationToken();
  const [showLandingPage, setShowLandingPage] = useState(true);
  const [conversationStartTime, setConversationStartTime] = useState<number | null>(null);
  const [micMuted, setMicMuted] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isVoiceMode, setIsVoiceMode] = useState(
    conversationDetail?.chatType === 'call' ? true : false
  );
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const { user } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    return () => {
      conversation.endSession();
    };
  }, []);

  useEffect(() => {
    conversationDetail?.chatType && conversationDetail?.chatType === 'call' && setIsVoiceMode(true);
    if (conversationDetail && conversationDetail.conversationId) {
      fetchConversationDetail();
    }
  }, [conversationDetail]);
  const fetchConversationDetail = async () => {
    try {
      const response = await conversationDetailMutation.mutateAsync({
        conversationId: conversationDetail?.conversationId as string,
      });
      setConversationStartTime(response.metadata.start_time_unix_secs);
      setMessages(response.transcript);
      setShowLandingPage(false);
      onChatLoaded(true);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  };
  useEffect(() => {
    if (
      messages.length &&
      conversationDetail?.chatType !== 'view' &&
      conversationDetail?.conversationId &&
      conversation.status !== 'connected'
    ) {
      startConversation();
    }
  }, [messages]);

  useEffect(() => {
    if (isVoiceMode && !conversationDetail) {
      startConversation();
    }
  }, [isVoiceMode]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || messages.length === 0) return;
    container.scrollTop = container.scrollHeight;
  }, [messages]);

  const conversation = useConversation({
    micMuted,
    textOnly: !isVoiceMode,
    onConnect: () => console.log('Connected'),
    onDisconnect: () => console.log('Disconnected'),
    onStatusChange: (status) => console.log('Status changed:', status),
    onMessage: (message) => {
      setMessages((prev) => [
        ...prev,
        {
          message: message.message,
          role: message.source === 'user' ? 'user' : 'agent',
          timestamp: Date.now(), // Add timestamp when receiving message
        },
      ]);
      console.log('Message:', message);
    },
    onError: (error) => console.error('Error:', error),
  });

  const sendContextualUpdate = useCallback(
    async (messages: TranscriptMessage[]) => {
      try {
        // Create a summary of all messages
        const summary = messages.map((turn) => `${turn.role}: ${turn.message}`).join(' | ');

        // Send the summary as context
        conversation.sendContextualUpdate(summary);
      } catch (error) {
        console.error('Failed to send contextual update:', error);
      }
    },
    [conversation]
  );

  useEffect(() => {
    console.log('conversation.status ', conversation.status);

    if (conversation.status === 'disconnected') {
      conversation.endSession();
      setShowLandingPage(true);
      setIsVoiceMode(false);
      setMessages([]);
      onChatLoaded(false);
      setMicMuted(false);
    }
  }, [conversation.status]);

  const startConversation = useCallback(async () => {
    onChatLoaded(true);
    try {
      const agentId = process.env.REACT_APP_ELEVENS_LAB_AGENT_ID as string;
      const baseConfig = {
        agentId,
        dynamicVariables: {
          user_name: user?.name || 'Guest',
          email: user?.email || '',
        },
        userId: user ? `${user?.id}-${user?.businessId}` : undefined,
      };

      let sessionConfig: any;

      if (isVoiceMode) {
        const { signedUrl } = await signedUrlMutation.mutateAsync({
          agent_id: agentId,
        });
        await navigator.mediaDevices.getUserMedia({ audio: true });

        sessionConfig = {
          ...baseConfig,
          connectionType: 'webrtc',
          signedUrl,
        };
      } else {
        const { token } = await conversationTokenMutation.mutateAsync({
          agent_id: agentId,
        });

        sessionConfig = {
          ...baseConfig,
          connectionType: 'websocket',
          conversationToken: token,
        };
      }

      await conversation.startSession(sessionConfig);

      if (conversationDetail?.conversationId && conversationDetail.chatType !== 'view') {
        await sendContextualUpdate(messages);
      }
    } catch (error) {
      console.error('Failed to start conversation:', error);
    }
  }, [
    conversation,
    isVoiceMode,
    user,
    conversationDetail,
    messages,
    signedUrlMutation,
    conversationTokenMutation,
  ]);

  const stopConversation = useCallback(async () => {
    await conversation.endSession();
    setShowLandingPage(true);
    setIsVoiceMode(false);
    setMessages([]);
    setMicMuted(false);
    onClearChat && onClearChat();
  }, [conversation]);

  const initiateVoiceMode = useCallback(() => {
    setIsVoiceMode(true);
    setShowLandingPage(false);
  }, []);

  const initiatechateMode = useCallback(() => {
    setIsVoiceMode(false);
    setShowLandingPage(false);
    startConversation();
  }, []);
  const sendMessage = useCallback(async () => {
    if (!message || !message.trim()) return;

    try {
      const newMessage: TranscriptMessage = {
        message: message,
        role: 'user',
        timestamp: Date.now(), // Add timestamp when sending message
      };
      setMessages((prev) => [...prev, newMessage]);
      await conversation.sendUserMessage(message);
      setMessage(null);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  }, [conversation, message]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent newline
      sendMessage();
    }
  };
  const showEmily =
    user?.permissions?.emily && user?.permissions?.emily?.showMenu === true ? true : false;

  return showLandingPage ? (
    <div className="petsked-chat-container landing-page">
      <div className="flex items-center gap-4 mb-6">
        <div className="avatar-container">
          <WelcomeBanner chatModeStarted={false} />
          {showEmily ? (
            <>
              <div className="action-initiator">
                <Button variant="primary" size="lg" onClick={initiateVoiceMode}>
                  Call Emily
                </Button>
                <Button variant="primary" size="lg" onClick={initiatechateMode}>
                  Chat with Emily
                </Button>
              </div>
              <div className="action-initiator history">
                <Button className="chat-history" variant="inverse" size="lg" onClick={showHistory}>
                  Chat History
                </Button>
              </div>
            </>
          ) : (
            <div className="action-initiator">
              <h5 className="text-danger text-center">
                You dont have permission to access this feature.
                <br />
                Please contact support.
              </h5>
            </div>
          )}
        </div>
      </div>
    </div>
  ) : (
    <div className={`wrapper-assistant ${isVoiceMode ? 'voice' : 'chat'}`}>
      <CloseIcon className="close-icon" title="End Conversation" onClick={stopConversation} />
      <div className="petsked-chat-container">
        <div className="flex items-center gap-4 mb-6">
          <div className="avatar-container">
            <WelcomeBanner chatModeStarted={true} />
          </div>
        </div>

        <div ref={containerRef} className="message-history">
          {messages.map(
            (msg, index) =>
              msg.message && (
                <div
                  key={`msg-${msg.timestamp || index}`}
                  className={`message-baloon ${msg.role} `}
                >
                  <div className="timestamp">
                    {msg.role === 'user' ? 'You' : 'Emily'}

                    {msg.timestamp
                      ? ' . ' + new Date(msg.timestamp).toLocaleTimeString()
                      : conversationStartTime
                      ? ' . ' +
                        Utils.formatDateStringTz(
                          new Date(
                            conversationStartTime * 1000 +
                              (msg.time_in_call_secs ? msg.time_in_call_secs : 0)
                          ).toISOString(),
                          undefined,
                          'MMM DD, yyyy hh:mm a'
                        )
                      : ''}
                  </div>
                  <div
                    className={`msg-content ${msg.role} ${
                      msg.role === 'user' ? 'ml-auto' : 'mr-auto'
                    }`}
                  >
                    <pre>{msg.message}</pre>
                  </div>
                </div>
              )
          )}
        </div>

        {(!conversationDetail || conversationDetail.chatType !== 'view') && (
          <div className="action-container flex gap-2">
            <span className="text-area-container">
              <textarea
                draggable={false}
                value={message || ''}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 p-2 border rounded"
                disabled={
                  conversation.status !== 'connected' || conversationDetail?.conversationId
                    ? false
                    : false
                }
                rows={2}
              />
            </span>
            <SendIcon className="send-icon" onClick={sendMessage} />
          </div>
        )}
        {/* {!conversationId && (
        )} */}
      </div>
      {isVoiceMode && (
        <div className="voice-container">
          <div className="flex flex-col items-center">
            <Lottie
              animationData={animationDataBg}
              loop={true}
              autoplay={true}
              className="logo-bg-animation-voice"
            />
            {/* <WelcomeBanner hideText={true} chatModeStarted={false} />{' '} */}
            <div className="controls">
              <AudioStatus isSpeaking={conversation.isSpeaking || false} />{' '}
              <span className="mic-icon-container" onClick={() => setMicMuted(!micMuted)}>
                {conversation.micMuted ? (
                  <AudioMutedIcon className="mic-icon muted" />
                ) : (
                  <AudioIcon className="mic-icon" />
                )}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
